/* Native CartForm/Shopify sections own all cart requests and ranking. This
 * dynamic child only owns stale-card removal, focus and native lazy cleanup.
 * The uncached data-merge parent survives every filled/empty cart transition.
 */
(() => {
  if (!window.customElements || customElements.get('lunya-cart-recommendations')) return;

  class LunyaCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.onFocus = event => { this.focusedControl = this.contains(event.target) ? event.target : null; };
      this.onChange = () => this.invalidate();
      this.onAdd = event => {
        // Native ProductForm emits add AFTER a successful bundled section merge.
        // Do not erase that fresh row. An unconfirmed add cannot attest to it.
        const ids = this.dataset.cartVariantIds.split(',');
        if (!event.detail?.variantId || !ids.includes(String(event.detail.variantId))) this.invalidate();
      };
      this.onMerge = () => queueMicrotask(() => {
        // Native trapFocus snapshots focusable nodes. Rebuild its boundaries
        // after the native cart merge replaces footer/recommendation controls.
        const drawer = this.closest('cart-drawer');
        const active = document.activeElement;
        if (!this.isConnected || !drawer?.hasAttribute('open')) return;
        const returnToDrawer = active === document.body || (drawer.contains(active) && !this.visible(active));
        // Rebuild even when focus was intentionally placed outside the drawer:
        // its newly merged footer must trap correctly when the shopper returns.
        trapFocus(drawer, returnToDrawer ? this.closeButton(drawer) || drawer : active, null, returnToDrawer);
      });
      // CartForm parses the incoming section into a detached document fragment
      // then copies HTML into merge targets. Native card constructors observe
      // that temporary subtree even though it never receives disconnect. Clean
      // it after parsing; a later genuine connection reinitializes normally.
      queueMicrotask(() => {
        if (!this.isConnected) {
          this.releaseNative();
          this.needsNativeReinit = true;
        }
      });
    }

    connectedCallback() {
      document.addEventListener('focusin', this.onFocus);
      document.addEventListener('on:cart:change', this.onChange);
      document.addEventListener('on:cart:error', this.onChange);
      document.addEventListener('on:cart:add', this.onAdd);
      this.drawer = this.closest('cart-drawer');
      this.drawer?.addEventListener('on:cart:after-merge', this.onMerge);
      if (this.needsNativeReinit) {
        this.querySelectorAll('carousel-slider').forEach(slider => slider.refresh());
        this.querySelectorAll('product-block').forEach(card => { if (!card.images) card.init(); });
        this.needsNativeReinit = false;
      }
      // Rendered on a `?page=N` request, where Shopify's shared paginate parameter would have
      // shifted the source windows: the Liquid left a marker instead of cards.
      if (this.querySelector('[data-lunya-recommendations-stale]')) this.refreshStale();
    }

    disconnectedCallback() {
      document.removeEventListener('focusin', this.onFocus);
      document.removeEventListener('on:cart:change', this.onChange);
      document.removeEventListener('on:cart:error', this.onChange);
      document.removeEventListener('on:cart:add', this.onAdd);
      this.drawer?.removeEventListener('on:cart:after-merge', this.onMerge);
      this.staleRequest?.abort();
      this.staleRequest = null;
      this.releaseNative();
      this.needsNativeReinit = true;
      this.restoreFocus(this.focusedControl, this.drawer);
      this.focusedControl = null;
    }

    releaseNative() {
      this.querySelectorAll('carousel-slider[loaded]').forEach(slider => slider.removeListeners());
      this.querySelectorAll('carousel-slider, product-block').forEach(element => element.lazyInitObserver?.disconnect());
    }

    visible(element) {
      return element && !element.disabled && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
    }

    closeButton(drawer) {
      return [...drawer.querySelectorAll('.js-close-drawer')].find(element => this.visible(element));
    }

    restoreFocus(control, drawer) {
      if (!control || !drawer) return;
      const href = control.getAttribute('href');
      const name = control.getAttribute('name');
      queueMicrotask(() => {
        if (!drawer.isConnected || !drawer.hasAttribute('open') || document.activeElement !== document.body) return;
        const target = [...drawer.querySelectorAll('lunya-cart-recommendations a[href], lunya-cart-recommendations button[name]')]
          .find(element => this.visible(element) && element.tabIndex >= 0 && !element.closest('[aria-hidden="true"], [inert]') &&
            (href ? element.getAttribute('href') === href : name && element.getAttribute('name') === name));
        (target || this.closeButton(drawer) || drawer).focus({ preventScroll: true });
      });
    }

    // Re-request this drawer section from the shop root, where no `page` parameter applies,
    // and take only this row from it. Native cart merges still own every other refresh; one
    // that lands first replaces this element, which aborts the request on disconnect.
    refreshStale() {
      const host = this.closest('[data-section-id]');
      const sectionId = host?.dataset.sectionId;
      if (!sectionId || !window.fetch || !window.DOMParser) return;
      this.staleRequest?.abort();
      const request = new AbortController();
      this.staleRequest = request;
      const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
      url.searchParams.set('sections', sectionId);
      fetch(url, { credentials: 'same-origin', signal: request.signal })
        .then(response => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((sections) => {
          if (this.staleRequest !== request || !this.isConnected || typeof sections?.[sectionId] !== 'string') return;
          const incoming = new DOMParser().parseFromString(sections[sectionId], 'text/html').querySelector('lunya-cart-recommendations');
          if (!incoming || incoming.querySelector('[data-lunya-recommendations-stale]')) return;
          this.releaseNative();
          this.dataset.cartVariantIds = incoming.dataset.cartVariantIds || '';
          this.innerHTML = incoming.innerHTML;
        })
        .catch(() => { /* Best effort: the row simply stays empty, as before any cart change. */ })
        .finally(() => { if (this.staleRequest === request) this.staleRequest = null; });
    }

    invalidate() {
      if (!this.isConnected) return;
      this.staleRequest?.abort();
      const active = this.contains(document.activeElement) ? document.activeElement : null;
      this.releaseNative();
      this.replaceChildren();
      this.restoreFocus(active, this.drawer);
    }
  }
  customElements.define('lunya-cart-recommendations', LunyaCartRecommendations);
})();
